// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { getDateLocale, parseDate } from '@shared/ui-kit/utils/format-date';
import dayjs from 'dayjs';

export type DocumentExpiryDot = 'none' | 'warning' | 'negative';

export interface DocumentExpiryHint {
  dot: DocumentExpiryDot;
  tooltip?: string;
}

const DAYS_BEFORE_MONTH_UNIT = 30;

const formatExpiryRelative = (
  expiry: dayjs.Dayjs,
  today: dayjs.Dayjs,
  locale: string,
): string => {
  const expiryDay = expiry.startOf('day');
  const todayDay = today.startOf('day');
  const diffDays = expiryDay.diff(todayDay, 'day');
  const absDays = Math.abs(diffDays);

  const rtf = new Intl.RelativeTimeFormat(getDateLocale(locale), {
    style: 'long',
    numeric: diffDays === 0 ? 'auto' : 'always',
  });

  if (absDays < DAYS_BEFORE_MONTH_UNIT) {
    return rtf.format(diffDays, 'day');
  }

  const diffMonths = expiryDay.diff(todayDay, 'month');

  if (Math.abs(diffMonths) < 12) {
    const months = diffMonths === 0 ? (diffDays > 0 ? 1 : -1) : diffMonths;

    return rtf.format(months, 'month');
  }

  const diffYears = expiryDay.diff(todayDay, 'year');
  const years = diffYears === 0 ? (diffDays > 0 ? 1 : -1) : diffYears;

  return rtf.format(years, 'year');
};

/** Tooltip copy and dot color for the expiry column. */
export const getDocumentExpiryHint = (
  expiryDate: string | null | undefined,
  locale: string,
  today = new Date(),
): DocumentExpiryHint => {
  if (!expiryDate) {
    return { dot: 'none' };
  }

  const expiry = parseDate(expiryDate);

  if (!expiry) {
    return { dot: 'none' };
  }

  const expiryDay = dayjs(expiry).startOf('day');
  const todayDay = dayjs(today).startOf('day');
  const diffDays = expiryDay.diff(todayDay, 'day');
  const tooltip = formatExpiryRelative(expiryDay, todayDay, locale);

  if (diffDays < 0) {
    return {
      dot: 'negative',
      tooltip,
    };
  }

  return {
    dot: 'warning',
    tooltip,
  };
};
